<template>
  <BasePopup :model-value="isOpened" @update:model-value="handleClickReject">
    <template #title>
      {{ props.title }}
    </template>
    <template v-if="props.text" #body>
      {{ props.text }}
    </template>
    <template #actions="{ rowStyle, columnStyle }">
      <div
        :style="
          props.actionsOptions?.direction === 'row' ? rowStyle : columnStyle
        "
      >
        <BaseButton
          v-for="(action, index) in actions"
          :key="index"
          v-bind="omit(action, ['type', 'prependIcon', 'appendIcon'])"
          :with-full="action.withFull === undefined ? true : action.withFull"
          :with-decor="action.withDecor === undefined ? true : action.withDecor"
          :data-testid="`${action.type}-confirm-dialog-btn`"
          @click="handleClick(action.type, action.handler)"
        >
          <template v-if="action.prependIcon" #prepend-icon>
            <BaseIcon
              v-bind="action.prependIcon"
              :name="action.prependIcon.name"
              :size="action.prependIcon?.size ?? '20px'"
            />
          </template>
          {{ action.text }}
          <template v-if="action.appendIcon" #append-icon>
            <BaseIcon
              v-bind="action.appendIcon"
              :name="action.appendIcon.name"
              :size="action.appendIcon?.size ?? '20px'"
            />
          </template>
        </BaseButton>
      </div>
    </template>
  </BasePopup>
</template>

<script setup lang="ts">
// types
import type { IProps as IButtonProps } from '../button/index.vue'
import type { IProps as IIconProps } from '../icon/index.vue'

// lib
import omit from 'lodash/omit'

// app
import BaseButton from '../button/index.vue'
import BaseIcon from '../icon/index.vue'
import BasePopup from '../popup/index.vue'

export interface IAction extends Omit<IButtonProps, 'type'> {
  text: string
  type: 'resolve' | 'reject'
  prependIcon?: IIconProps
  appendIcon?: IIconProps
  handler?: () => void
}

interface IActionsOptions {
  direction: 'column' | 'row'
}

export interface Props {
  title: string
  text?: string
  actions: IAction[]
  actionsOptions?: IActionsOptions
}

const props = withDefaults(defineProps<Props>(), {
  actionsOptions: () => ({ direction: 'row' }),
})
const emit = defineEmits<{
  (e: 'clickResolve'): void
  (e: 'clickReject'): void
}>()

const isOpened = ref(false)
let resolveFn: (value: boolean) => void = () => {}

function handleClick(type: IAction['type'], handler?: () => void) {
  if (type === 'resolve') {
    handler?.()
    resolveFn(true)
    emit('clickResolve')
    isOpened.value = false
    return
  }

  handleClickReject()
}

function handleClickReject() {
  emit('clickReject')
  resolveFn(false)
  isOpened.value = false
}

async function open() {
  isOpened.value = true

  return new Promise((resolve) => {
    resolveFn = resolve
  })
}

defineExpose({
  open,
})
</script>
